import React, { useState, useEffect } from 'react';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Lightbulb, Rocket, Book } from 'react-bootstrap-icons';
import LandingNav from '../components/shared/LandingNav';
import SiteFooter from '../components/shared/SiteFooter';
import { useTheme } from '../contexts/ThemeContext';
import useIsMobile from '../hooks/useIsMobile';
import { isPrerendering } from '../utils/prerendering';
import { fetchLearnSnapshot } from '../services/staticContent';
import Seo from '../components/Seo';
import './Learn.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Learn = () => {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [learnContent, setLearnContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build-time snapshot first so crawlers get real content, then refresh live.
  useEffect(() => {
    let cancelled = false;

    const fetchLearnContent = async () => {
      const snapshot = await fetchLearnSnapshot();
      if (cancelled) return;

      const hasSnapshot = Boolean(snapshot?.content);
      if (hasSnapshot) {
        setLearnContent(snapshot.content);
        setError(null);
        setLoading(false);
      }

      if (isPrerendering() || !BACKEND_URL) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/learn/content`);
        if (!response.ok) {
          throw new Error('Failed to fetch learn content');
        }
        const data = await response.json();
        if (!data.success || !data.content) {
          throw new Error('Invalid response format');
        }
        if (!cancelled) {
          setLearnContent(data.content);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching learn content:', err);
        if (!cancelled && !hasSnapshot) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLearnContent();
    return () => {
      cancelled = true;
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
        <LandingNav />
        <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <Spinner animation="border" style={{ color: colors.primary }} />
          </div>
        </Container>
        <SiteFooter />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
        <LandingNav />
        <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
          <Alert variant="danger" style={{ backgroundColor: colors.bgCard, borderColor: colors.danger, color: colors.text }}>
            <Alert.Heading>Error Loading Content</Alert.Heading>
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                backgroundColor: colors.primary,
                color: '#fff',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </Alert>
        </Container>
        <SiteFooter />
      </div>
    );
  }

  // No content state
  if (!learnContent) {
    return (
      <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
        <LandingNav />
        <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
          <Alert variant="warning" style={{ backgroundColor: colors.bgCard, borderColor: colors.warning, color: colors.text }}>
            No content available at this time.
          </Alert>
        </Container>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="learn-page" style={{ minHeight: '100vh', backgroundColor: colors.bgPrimary }}>
      <Seo
        title="Learn — Trading Discipline Guides | PrecisionTrader"
        description="Guides and articles on building trading discipline, managing risk, and getting the most out of PrecisionTrader."
        canonicalPath="/learn"
      />
      <LandingNav />
      
      <Container style={{ maxWidth: '1200px', padding: isMobile ? '1rem' : '2rem', paddingTop: isMobile ? '1rem' : '4rem', marginTop: '2rem' }}>
        {/* Page Header */}
        <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <h1 style={{ 
            color: colors.text, 
            fontSize: isMobile ? '2rem' : '3rem',
            marginBottom: '0.5rem',
            fontWeight: '700'
          }}>
            Learning Center
          </h1>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: isMobile ? '1rem' : '1.2rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            Master trading discipline and learn how to use PrecisionTrader effectively
          </p>
        </div>

        {/* Content - Section Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Trading Fundamentals Card */}
          <Link
            to="/learn/trading-fundamentals"
            style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
          >
            <div style={{
              padding: '2rem',
              backgroundColor: colors.bgCard,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              transition: 'all 0.2s',
              cursor: 'pointer',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#FFD700';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px rgba(255, 215, 0, 0.2)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 215, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem',
                border: '1px solid rgba(255, 215, 0, 0.3)'
              }}>
                <Lightbulb size={28} color="#FFD700" />
              </div>
              <h2 style={{
                color: colors.text,
                fontSize: isMobile ? '1.5rem' : '1.75rem',
                marginBottom: '0.75rem',
                fontWeight: '700'
              }}>
                Trading Fundamentals
              </h2>
              <p style={{
                color: colors.textMuted,
                fontSize: isMobile ? '0.95rem' : '1rem',
                margin: 0,
                marginBottom: '1.5rem',
                lineHeight: '1.6',
                flex: 1
              }}>
                Master trading discipline and learn structured approaches to finding your edge, managing risk, and building consistent trading routines.
              </p>
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                {learnContent?.tradingFundamentals?.subsections?.slice(0, 6).map((sub) => (
                  <Link
                    key={sub.id}
                    to={`/learn/trading-fundamentals#${sub.id}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: colors.bgSecondary,
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      color: colors.textMuted,
                      border: `1px solid ${colors.border}`,
                      textDecoration: 'none',
                      display: 'inline-block',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary + '20';
                      e.currentTarget.style.borderColor = colors.primary;
                      e.currentTarget.style.color = colors.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.bgSecondary;
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.color = colors.textMuted;
                    }}
                  >
                    {sub.title}
                  </Link>
                ))}
              </div>
            </div>
          </Link>

          {/* Quick Start Card */}
          <Link
            to="/learn/quickstart"
            style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
          >
            <div style={{
              padding: '2rem',
              backgroundColor: colors.bgCard,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              transition: 'all 0.2s',
              cursor: 'pointer',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#2196F3';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px rgba(33, 150, 243, 0.2)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                backgroundColor: 'rgba(33, 150, 243, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem',
                border: '1px solid rgba(33, 150, 243, 0.3)'
              }}>
                <Rocket size={28} color="#2196F3" />
              </div>
              <h2 style={{
                color: colors.text,
                fontSize: isMobile ? '1.5rem' : '1.75rem',
                marginBottom: '0.75rem',
                fontWeight: '700'
              }}>
                Quick Start
              </h2>
              <p style={{
                color: colors.textMuted,
                fontSize: isMobile ? '0.95rem' : '1rem',
                margin: 0,
                marginBottom: '1.5rem',
                lineHeight: '1.6',
                flex: 1
              }}>
                Get started with PrecisionTrader. Learn how to connect your broker, select your account, and place your first bracket order.
              </p>
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                {learnContent?.quickStart?.subsections?.slice(0, 2).map((sub) => (
                  <Link
                    key={sub.id}
                    to={`/learn/quickstart#${sub.id}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      padding: '0.4rem 0.8rem',
                      backgroundColor: colors.bgSecondary,
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      color: colors.textMuted,
                      border: `1px solid ${colors.border}`,
                      textDecoration: 'none',
                      display: 'inline-block',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.primary + '20';
                      e.currentTarget.style.borderColor = colors.primary;
                      e.currentTarget.style.color = colors.primary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = colors.bgSecondary;
                      e.currentTarget.style.borderColor = colors.border;
                      e.currentTarget.style.color = colors.textMuted;
                    }}
                  >
                    {sub.title}
                  </Link>
                ))}
              </div>
            </div>
          </Link>

          {/* Software Guides Card */}
          <Link
            to="/learn/software-guides"
            style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
          >
            <div style={{
              padding: '2rem',
              backgroundColor: colors.bgCard,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`,
              transition: 'all 0.2s',
              cursor: 'pointer',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#4CAF50';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px rgba(76, 175, 80, 0.2)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '12px',
                backgroundColor: 'rgba(76, 175, 80, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem',
                border: '1px solid rgba(76, 175, 80, 0.3)'
              }}>
                <Book size={28} color="#4CAF50" />
              </div>
              <h2 style={{
                color: colors.text,
                fontSize: isMobile ? '1.5rem' : '1.75rem',
                marginBottom: '0.75rem',
                fontWeight: '700'
              }}>
                Software Guides
              </h2>
              <p style={{
                color: colors.textMuted,
                fontSize: isMobile ? '0.95rem' : '1rem',
                margin: 0,
                lineHeight: '1.6',
                flex: 1
              }}>
                Learn how to use PrecisionTrader's features including risk management tools, bracket orders, position sizing, and more.
              </p>
            </div>
          </Link>
        </div>
      </Container>

      <SiteFooter />
    </div>
  );
};

export default Learn;
